import './ComboEditor.less';
import React, {useState} from 'react';
import __ from "../../i18n";
import CustomForm from "../../../bootstrap/components/custom-form/CustomForm";
import Request from "../../../bootstrap/api/Request";
import Toasts, {TOAST_SUCCESS} from "../../../bootstrap/scomponents/toasts/Toasts";
import ComboCreateParcel from "../../api/endpoints/combo/ComboCreateParcel";
import ComboUpdateParcel from "../../api/endpoints/combo/ComboUpdateParcel";
import {slugify} from '../../../bootstrap/utils/string';
import routes from "../../routing/routes";
import {CURRENCY_INPUT_UNIT} from "../../view-constants/currency";
import visibilities, {VISIBILITY_PUBLIC} from "../../model-constants/combo/visibilities";
import {MAX_NUM} from "../../../bootstrap/utils/stats";
import listToSearchParcel from '../../api/convert/listToSearchParcel';
import listToFetchParcel from '../../api/convert/listToFetchParcel';
import PromotionListParcel from '../../api/endpoints/promotion/PromotionListParcel';
import CourseListParcel from '../../api/endpoints/course/CourseListParcel';


export default function ComboEditor({recordId = null, defaultValues = {}, onDone, onCancel}) {

    const isCreation = recordId === null;

    const [values, setValues] = useState(defaultValues);

    const slug = values.name ? slugify(values.name) : '';

    const fieldsConfig = {
        course_ids: {
            name: __('Courses'),
            type: 'multiAjaxSelect',
            defaultValue: defaultValues.course_ids,
            data: {
                ItemsFetchParcel: listToFetchParcel(CourseListParcel),
                ItemsSearchParcel: listToSearchParcel(CourseListParcel)
            },
            options: {
                prompt: __('Select courses'),
                clearable: true,
                renderOption: t => t.name
            },
            rules: {
                required: true
            }
        },
        name: {
            name: __('Name'),
            type: 'text',
            defaultValue: defaultValues.name,
            rules: {
                required: true,
                maxLength: 191,
            },
            options: {
                appearance: 'box'
            },
            valueHint: slug !== '' ? __('URL: ::url', {url: routes.combo.getUrl(slug)}) : undefined,
        },
        avatar_image_id: {
            name: __('Avatar image'),
            type: 'image',
            defaultValue: defaultValues.avatar_image_id,
            rules: {
                required: true
            }
        },
        price: {
            name: __('Price'),
            type: 'number',
            defaultValue: defaultValues.price,
            rules: {
                required: true,
                min: 0,
                max: MAX_NUM
            },
            options: {
                unit: CURRENCY_INPUT_UNIT,
                appearance: 'box'
            },
            valueHint: values.price !== null && values.price >= 0 ? __('::amount(CURRENCY, symbol)', {amount: values.price}) : undefined
        },
        description: {
            name: __('Description'),
            type: 'text',
            defaultValue: defaultValues.description,
            rules: {
                required: true,
                maxLength: 191
            },
            options: {
                appearance: 'box'
            }
        },
        visibility: {
            name: __('Visibility'),
            type: 'singleSelect',
            defaultValue: defaultValues.visibility || VISIBILITY_PUBLIC,
            rules: {
                required: true
            },
            data: {
                options: visibilities
            }
        },
        priority: {
            name: __('Priority'),
            type: 'number',
            defaultValue: defaultValues.priority || 0,
            rules: {
                required: true
            },
            options: {
                appearance: 'box',
                prompt: __('Higher priority will be placed before')
            },
        },
        introduction: {
            name: __('Introduction'),
            type: 'richText',
            defaultValue: defaultValues.introduction,
            rules: {
                required: false
            }
        },
        promotion_id: {
            name: __('Promotion'),
            type: 'singleAjaxSelect',
            defaultValue: defaultValues.promotion_id,
            data: {
                ItemsSearchParcel: listToSearchParcel(PromotionListParcel),
                ItemsFetchParcel: listToFetchParcel(PromotionListParcel),
            },
            options: {
                renderOption: t => (
                    <>
                        {t.name}
                        <br/>
                        <small>{__('Discount: ::percent%', {percent: t.discount_percentage})}</small>
                    </>
                ),
                clearable: true,
            },
            rules: {
                required: false
            }
        },
    };

    const layoutConfig = [
        'name',
        'course_ids',
        'avatar_image_id',
        {
            children: [
                'price',
                'promotion_id',
            ],
            direction: 'row',
            alignItems: 'start',
            itemWeights: [0.3, 0.7],
        },
        'description',
        {
            children: [
                'visibility',
                'priority',
            ],
            direction: 'row',
            alignItems: 'start',
            itemWeights: [0.5, 0.5],
        },
        'introduction',
    ];

    const submit = (values, onSuccess, onError) => {

        const submittedValues = {
            ...values,
            slug
        };

        if (isCreation) {
            Request.add(new ComboCreateParcel(submittedValues))
                .then((data) => {
                    Toasts.add(TOAST_SUCCESS, __('Your combo was added successfully'));
                    onSuccess();
                    onDone(data);
                })
                .catch(error => {
                    onError(error.getMessage());
                });

            return;
        }

        Request.add(new ComboUpdateParcel(recordId, submittedValues))
            .then((data) => {
                Toasts.add(TOAST_SUCCESS, __('Your changes have been saved successfully'));
                onSuccess();
                onDone(data);
            })
            .catch(error => {
                onError(error.getMessage());
            });
    };

    const cancel = () => {
        onCancel();
    };

    return <div className="combo-editor">
        <div className="sub-title">{__('Combo information')}</div>
        <CustomForm
            fieldsConfig={fieldsConfig}
            layoutConfig={layoutConfig}
            submitChanges={submit}
            cancel={cancel}
            submitText={isCreation ? __('Add combo') : __('Save changes')}
            onChange={setValues}
            onInitialized={setValues}
        />
    </div>;
}
